"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Navbar from "@/components/Navbar";
import ContactForm from "@/components/ContactForm";
import api from "@/lib/api";

type AboutUsItem = {
  title: string;
  body: string;
};

type AboutUsSection = {
  title: string;
  items: AboutUsItem[];
};

type AboutUsResponse = {
  status: string;
  data?: {
    sections?: AboutUsSection[];
  };
};

type TotalPlaysResponse = {
  total_duration?: number;
};

export default function AboutUsPage() {
  const [showMore, setShowMore] = useState(false);

  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [collabInvite, setCollabInvite] = useState<number | null>(null);

  const [totalPlays, setTotalPlays] = useState("؟");

  const [faqItems, setFaqItems] = useState<AboutUsItem[]>([]);
  const [collaborationItems, setCollaborationItems] = useState<
    AboutUsItem[]
  >([]);

  const [aboutLoading, setAboutLoading] = useState(true);
  const [aboutError, setAboutError] = useState(false);

  async function fetchTotalPlays() {
    try {
      const res = (await api("/api/v1/total/plays", {
        method: "GET",
      })) as TotalPlaysResponse;

      if (res?.total_duration !== undefined) {
        setTotalPlays(
          (res.total_duration).toLocaleString("en-US")
        );
      }
    } catch (error) {
      console.error("خطا در دریافت آمار:", error);
    }
  }

  async function fetchAboutUs() {
    try {
      setAboutLoading(true);
      setAboutError(false);

      const res = (await api("/api/v1/pages/about-us", {
        method: "GET",
      })) as AboutUsResponse;

      if (res?.status !== "ok") {
        throw new Error("پاسخ نامعتبر از API");
      }

      const sections = res?.data?.sections ?? [];

      const faqSection = sections.find(
        (section) => section.title === "سوالات متداول"
      );

      const collaborationSection = sections.find(
        (section) => section.title === "دعوت به همکاری"
      );

      setFaqItems(faqSection?.items ?? []);
      setCollaborationItems(collaborationSection?.items ?? []);
    } catch (error) {
      console.error("خطا در دریافت اطلاعات درباره ما:", error);
      setAboutError(true);
    } finally {
      setAboutLoading(false);
    }
  }

  useEffect(() => {
    fetchTotalPlays();
    fetchAboutUs();
  }, []);

  return (
    <>
      <Navbar showBack />

      <div className="p-5 text-white md:mx-auto md:max-w-xl">
        {/* تصویر بالای صفحه */}
        <div className="mx-auto w-max">
          <Image
            width={300}
            height={300}
            className="rounded-full md:max-w-[20rem]"
            src="/assets/about-2.jpg"
            alt="about us"
          />
        </div>

        <div className="mt-5 mb-5 px-4 text-center leading-relaxed sm:mt-10 sm:mb-5 sm:text-base md:text-lg">
          قصه شب{" "}
          <span className="text-yellow-500">{totalPlays}</span>{" "}
          دقیقه شنیده شده است!
        </div>

        {/* دو پاراگراف اول */}
        <p className="mt-7 text-justify leading-7">
          در قصه‌شب تلاش می‌کنیم تا بهترین داستان‌های ایران و جهان را در
          قالب داستان صوتی تهیه کنیم و در اختیار همه کودکان این سرزمین قرار
          دهیم؛ از شهرهای بزرگ تا روستاها و مناطق عشایری، و حتی خارج از
          مرزهای ایران.
        </p>

        {/* دکمه بیشتر */}
        {!showMore && (
          <button
            type="button"
            onClick={() => setShowMore(true)}
            className="mt-2 text-blue-300"
          >
            بیشتر...
          </button>
        )}

        {/* متن اضافه */}
        {showMore && (
          <div className="mt-3 text-justify leading-7 text-gray-300">
            <p>
              در فاز اول، بیش از 500 داستان صوتی کودک در قصه شب منتشر شده
              است. در فاز بعدی، تلاش خواهیم کرد تا این اپلیکیشن را برای
              استفاده کودکان نابینا و کم‌بینا نیز مناسب‌سازی کنیم. باعث
              افتخار و خوشحالی ماست که هم‌اکنون تعدادی از این عزیزان در
              مراکز کودکان نابینا و کم‌بینای شهرهای «کاشان» و «رشت»، به‌صورت
              «رایگان» از قصه‌شب استفاده می‌کنند. قصه‌شب یک مجموعه کاملاً
              خصوصی و مستقل است و با حمایت شما عزیزان، تلاش می‌کند تا همیشه
              محتوایی امن، باکیفیت و بدون تبلیغات مزاحم به کودکان ارائه دهد.
            </p>

            <div>قصه‌شب، برای همه کودکان این سرزمین</div>
          </div>
        )}

        {/* دکمه‌های دانلود */}
        <div className="mt-7 flex items-center justify-center gap-2 md:gap-4">
          {/* Direct Android */}
          <a
            href="/get-app"
            className="flex items-center rounded-lg bg-white px-2 py-1 text-black shadow md:px-3"
          >
            <Image
              src="/assets/android-2.png"
              alt="Android"
              width={32}
              height={32}
              className="md:h-10 md:w-10"
            />

            <div className="mr-2 text-right leading-4">
              <p className="text-[9px] md:text-[10px]">دانلود مستقیم</p>
              <p className="text-[11px] font-bold md:text-[12px]">
                Android
              </p>
            </div>
          </a>

          {/* Bazar */}
          <a
            href="https://cafebazaar.ir/app/org.nazariani.qeseyeshab"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center rounded-lg bg-white px-2 py-1 text-black shadow md:px-3"
          >
            <Image
              src="/assets/bazar.png"
              alt="Bazaar"
              width={32}
              height={32}
              className="md:h-10 md:w-10"
            />

            <div className="mr-2 text-right leading-4">
              <p className="text-[9px] md:text-[10px]">دریافت از</p>
              <p className="text-[11px] font-bold md:text-[12px]">
                بازار
              </p>
            </div>
          </a>

          {/* App Store */}
          <a
            href="/"
            className="flex select-none items-center rounded-lg bg-white px-2 py-1 text-black shadow md:px-3"
          >
            <Image
              src="/assets/app-store.png"
              alt="App Store"
              width={32}
              height={32}
              className="md:h-10 md:w-10"
            />

            <div className="mr-2 text-right leading-4">
              <p className="text-[9px] md:text-[10px]">به‌زودی</p>
              <p className="text-[11px] font-bold md:text-[12px]">
                App Store
              </p>
            </div>
          </a>
        </div>

        {/* FAQ */}
        <div className="mt-10">
          <h2 className="mb-3 text-xl font-bold">سؤالات متداول</h2>

          {aboutLoading && (
            <p className="py-4 text-center text-gray-300">
              در حال دریافت سؤالات متداول...
            </p>
          )}

          {!aboutLoading && aboutError && (
            <div className="py-4 text-center text-red-300">
              دریافت سؤالات متداول با خطا مواجه شد.

              <button
                type="button"
                onClick={fetchAboutUs}
                className="mx-auto mt-3 block rounded-lg bg-yellow-500 px-4 py-2 text-sm text-black"
              >
                تلاش مجدد
              </button>
            </div>
          )}

          {!aboutLoading &&
            !aboutError &&
            faqItems.length === 0 && (
              <p className="py-4 text-center text-gray-300">
                سؤالی برای نمایش وجود ندارد.
              </p>
            )}

          {!aboutLoading &&
            !aboutError &&
            faqItems.map((item, index) => (
              <div
                key={`${item.title}-${index}`}
                className="border-b border-gray-700 py-3"
              >
                <button
                  type="button"
                  className="flex w-full items-center justify-between text-right"
                  onClick={() =>
                    setOpenFAQ(openFAQ === index ? null : index)
                  }
                >
                  <span>{item.title}</span>

                  <span className="text-xl text-yellow-400">
                    {openFAQ === index ? "−" : "+"}
                  </span>
                </button>

                {openFAQ === index && (
                  <p className="mt-2 whitespace-pre-line text-justify text-sm text-gray-300">
                    {item.body}
                  </p>
                )}
              </div>
            ))}
        </div>

        {/* دعوت به همکاری */}
        <div className="mt-10">
          <h2 className="mb-3 text-xl font-bold">دعوت به همکاری</h2>

          {aboutLoading && (
            <p className="py-4 text-center text-gray-300">
              در حال دریافت اطلاعات همکاری...
            </p>
          )}

          {!aboutLoading &&
            !aboutError &&
            collaborationItems.length === 0 && (
              <p className="py-4 text-center text-gray-300">
                موردی برای نمایش وجود ندارد.
              </p>
            )}

          {!aboutLoading &&
            !aboutError &&
            collaborationItems.map((item, index) => (
              <div
                key={`${item.title}-${index}`}
                className="border-b border-gray-700 py-3"
              >
                <button
                  type="button"
                  className="flex w-full items-center justify-between text-right"
                  onClick={() =>
                    setCollabInvite(
                      collabInvite === index ? null : index
                    )
                  }
                >
                  <span>{item.title}</span>

                  <span className="text-xl text-yellow-400">
                    {collabInvite === index ? "−" : "+"}
                  </span>
                </button>

                {collabInvite === index && (
                  <p className="mt-2 whitespace-pre-line text-justify text-sm text-gray-300">
                    {item.body}
                  </p>
                )}
              </div>
            ))}
        </div>

        {/* فرم تماس */}
        <section className="mt-10">
          <ContactForm />
        </section>

        {/* نمادها */}
        <div className="mx-5 mt-8 flex items-center justify-center gap-5">
          <Image
            alt="logo-samandehi"
            src="/assets/samandehi.png"
            width={90}
            height={90}
          />
         <a referrerPolicy='origin' target='_blank' href='https://trustseal.enamad.ir/?id=8173751&Code=ZDMrFe6JWprVNn6EJB3Hhde1Jaa9o8Yi'>                  
          <img referrerPolicy='origin' src='https://trustseal.enamad.ir/logo.aspx?id=8173751&Code=ZDMrFe6JWprVNn6EJB3Hhde1Jaa9o8Yi'
            style={{cursor: "pointer" }}
            alt="enamad"
            width={90}
            height={90}
            />
         </a>          
        </div>

        {/* کپی‌رایت */}
        <div className="mt-3 text-center">
          &#169; کلیه حقوق برای{" "}
          <a
            href="https://nazariani.org"
            target="_blank"
            rel="noopener noreferrer"
            className="text-c3"
          >
            «بنیاد نظریانی»
          </a>{" "}
          محفوظ است.
        </div>
      </div>
    </>
  );
}
